import React, { useState } from "react";
import { MAX_PERSON_NAME_LENGTH, PERSON_TYPES, toPersonIntro } from "./personIntro";
import "./PersonIntroDialog.scss";

interface PersonIntroDialogProps {
  // Pre-fills the type with the one used last, so a run of commentators is a name each.
  initialType?: string;
  onClose: () => void;
  // Called with the trimmed name and the type's backend value; the caller closes the dialog.
  onSubmit: (personName: string, personType: string) => void | Promise<void>;
}

// "Person Intro" dialog: the streamer types a name and picks what the person is; the ticker shows the name with
// the type under it.
const PersonIntroDialog: React.FC<PersonIntroDialogProps> = ({ initialType, onClose, onSubmit }) => {
  const [name, setName] = useState("");
  const [type, setType] = useState(initialType ?? PERSON_TYPES[0].value);
  const [submitting, setSubmitting] = useState(false);

  const intro = toPersonIntro(name, type);

  const submit = async () => {
    if (!intro || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(intro.personName, intro.personType);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sd-dialog-overlay" onClick={onClose}>
      <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="sd-dialog__header">
          <h3 className="sd-dialog__title">Person Intro</h3>
          <button type="button" className="sd-dialog__close" onClick={onClose}>
            ✕
          </button>
        </div>

        <label className="sd-dialog__label" htmlFor="sd-person-name">
          Name
        </label>
        <input
          id="sd-person-name"
          className="sd-person__input"
          type="text"
          autoFocus
          maxLength={MAX_PERSON_NAME_LENGTH}
          placeholder="e.g. Shobith A K"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />

        <p className="sd-dialog__label">Type</p>
        <div className="sd-person__types" role="radiogroup" aria-label="Person type">
          {PERSON_TYPES.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={type === option.value}
              className={`sd-person__type${type === option.value ? " sd-person__type--active" : ""}`}
              onClick={() => setType(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="sd-dialog__actions">
          <button type="button" className="sd-dialog__btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="sd-dialog__btn-confirm" disabled={!intro || submitting} onClick={submit}>
            Show on Ticker
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonIntroDialog;
